'use client';

import {
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { MeasureAccordion } from './MeasureAccordion';
import { MeasureFeatureCarousel } from './MeasureFeatureCarousel';
import { MeasureFeatureMediaStage } from './MeasureFeatureMedia';
import { MEASURE_DEFAULT_OPEN, type MeasureFeature } from './measureMedia';
import styles from './Measures.module.css';

export function MeasureCategoryRow({
  imageLeft,
  title,
  body,
  bodyMobile,
  imageAlt,
  note,
  footer,
  features,
  carouselLabel,
}: {
  imageLeft: boolean;
  title: string;
  body: string;
  bodyMobile: string;
  imageAlt: string;
  note?: string;
  footer?: ReactNode;
  features: MeasureFeature[];
  carouselLabel: string;
}) {
  const { headerHeight = 76 } = useContext(GlobalUIContext);
  const mediaRef = useRef<HTMLDivElement>(null);
  const [mediaHalf, setMediaHalf] = useState(310);
  const [openId, setOpenId] = useState<string | null>(MEASURE_DEFAULT_OPEN);
  const [hintId, setHintId] = useState<string | null>(null);
  const onIntent = (id: string) => {
    const { connection } = navigator as Navigator & {
      connection?: { saveData?: boolean };
    };
    if (!connection?.saveData) setHintId(id);
  };

  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    const update = () => setMediaHalf(el.getBoundingClientRect().height / 2);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const active =
    features.find(
      (feature) => feature.id === (openId ?? MEASURE_DEFAULT_OPEN),
    ) ?? features[0];

  return (
    <article
      className={
        imageLeft ? `${styles.row} ${styles.rowImageLeft}` : styles.row
      }
    >
      <div className={styles.copy}>
        <div className={styles.copyHead} data-hiw-reveal-group>
          <h3 className={styles.categoryTitle} data-hiw-reveal="full">
            {title}
          </h3>
          <p
            className={`${styles.categoryBody} ${styles.categoryBodyDesktop}`}
            data-hiw-reveal="subtle"
          >
            {body}
          </p>
          <p
            className={`${styles.categoryBody} ${styles.categoryBodyMobile}`}
            data-hiw-reveal="subtle"
          >
            {bodyMobile}
          </p>
          {note ? <p className={styles.categoryNote}>{note}</p> : null}
          {footer ? <div className={styles.categoryLink}>{footer}</div> : null}
        </div>
        <div className={styles.accordion}>
          <MeasureAccordion
            features={features}
            openId={openId}
            onOpenChange={setOpenId}
            onIntent={onIntent}
          />
        </div>
      </div>
      <div
        ref={mediaRef}
        className={styles.media}
        style={
          {
            '--measure-sticky-top': `${headerHeight + 24}px`,
            '--measure-media-half': `${mediaHalf}px`,
          } as CSSProperties
        }
      >
        {active ? (
          <MeasureFeatureMediaStage
            feature={active}
            hint={features.find((feature) => feature.id === hintId)}
            imageAlt={imageAlt}
            sizes="(min-width: 992px) 640px, 100vw"
            imageLeft={imageLeft}
          />
        ) : null}
      </div>
      <div className={styles.carousel}>
        <MeasureFeatureCarousel
          features={features}
          imageAlt={imageAlt}
          label={carouselLabel}
        />
      </div>
    </article>
  );
}
